import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import {
  ChevronRightIcon,
  ChevronDownIcon,
  FolderIcon,
  DocumentIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentPlusIcon,
} from "@heroicons/react/24/outline";
import { documentDisciplines } from "../../../utils/documentDisciplines";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { getDisciplines, createDiscipline } from "../../../api/hse/disciplines";
import { getNextChapterNumber, createChapter, updateChapter, deleteChapter } from "../../../api/hse/chapters";
import { createCategory, updateCategory } from "../../../api/hse/categories";

/** Match category from tree node (id or code; tolerate string/number mismatch). */
const categoryMatchesId = (cat, categoryId) =>
  cat.id === categoryId ||
  cat.code === categoryId ||
  (categoryId != null &&
    (String(cat.id) === String(categoryId) || String(cat.code) === String(categoryId)));

/** Match chapter numbers from API (string) vs UI (possibly number). */
const chapterNumbersMatch = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();

/**
 * Find a category across all loaded disciplines (needed when "Alle" is selected or id types differ).
 * Prefer categories under preferredDiscipline when provided.
 */
const findCategoryInDisciplines = (disciplinesList, categoryId, preferredDiscipline) => {
  const pickFrom = (d) => d?.categories?.find((cat) => categoryMatchesId(cat, categoryId));
  if (preferredDiscipline) {
    const fromPreferred = pickFrom(preferredDiscipline);
    if (fromPreferred) return fromPreferred;
  }
  for (const d of disciplinesList || []) {
    const found = pickFrom(d);
    if (found) return found;
  }
  return null;
};

const toNumericId = (value) => {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s.match(/^\d+$/)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const compareChapterNumbers = (a, b) => {
  const pa = String(a ?? "")
    .split(".")
    .map((x) => parseInt(x, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
  const pb = String(b ?? "")
    .split(".")
    .map((x) => parseInt(x, 10))
    .map((n) => (Number.isFinite(n) ? n : 0));

  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] ?? -1;
    const vb = pb[i] ?? -1;
    if (va !== vb) return va - vb;
  }
  return 0;
};

const findChapterIdByNumber = (chapters, targetNumber) => {
  for (const ch of chapters || []) {
    if (chapterNumbersMatch(ch.number, targetNumber)) return ch.id;
    const nested = ch.sub_chapters || ch.subChapters;
    const found = findChapterIdByNumber(nested, targetNumber);
    if (found != null) return found;
  }
  return null;
};

const SIDEBAR_STATE_KEY = "promang:hse-documents:sidebar";

const readSidebarState = () => {
  try {
    const raw = window.sessionStorage.getItem(SIDEBAR_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const toStoredSet = (value) => new Set(Array.isArray(value) ? value : []);

/**
 * DocumentSidebar - Sidebar med fag/bransje-selector og treeview
 * 
 * Props:
 * @param {Function} onDisciplineSelect - Callback når fag/bransje velges (disciplineId)
 * @param {Function} onCategorySelect - Callback når kategori velges
 * @param {Function} onChapterSelect - Callback når kapittel velges (categoryId, chapterNumber)
 * @param {Function} onAddChapter - Callback når "+"-knapp klikkes for å legge til kapittel (categoryId, parentChapterNumber)
 * @param {Function} onEditChapter - Callback når kapittel redigeres (categoryId, chapterNumber, newData)
 * @param {Function} onDeleteChapter - Callback når kapittel slettes (categoryId, chapterNumber)
 * @param {Function} onAddDocument - Callback når "Legg til dokument" klikkes (categoryId, chapterNumber)
 * @param {Number|String} selectedDisciplineId - ID for valgt discipline (fra parent component)
 */
export const DocumentSidebar = ({ 
  onDisciplineSelect,
  onCategorySelect, 
  onChapterSelect, 
  onAddChapter: _onAddChapter,
  onEditChapter: _onEditChapter,
  onDeleteChapter: _onDeleteChapter,
  onAddDocument,
  selectedDisciplineId
}) => {
  const { t } = useTranslation();
  const initialSidebarStateRef = useRef(readSidebarState());
  
  // Fag/Bransje-struktur - hent fra API
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use selectedDisciplineId from props if provided, otherwise use internal state
  const [internalSelectedDiscipline, setInternalSelectedDiscipline] = useState(null);
  const selectedDiscipline = selectedDisciplineId !== undefined ? selectedDisciplineId : internalSelectedDiscipline;

  // Fetch disciplines on mount
  useEffect(() => {
    fetchDisciplines({ initial: true });
  }, []);
  
  // Sync selectedDiscipline when selectedDisciplineId prop changes
  useEffect(() => {
    if (selectedDisciplineId !== undefined && selectedDisciplineId !== selectedDiscipline) {
      // Parent is controlling the selection, no need to update internal state
    }
  }, [selectedDisciplineId]);

  const fetchDisciplines = async (opts = {}) => {
    const { silent = false, initial = false } = opts;
    const showGlobalLoading = !silent && (initial || disciplines.length === 0);
    try {
      // Avoid full-sidebar "reload" UX on every small mutation; only show global loading on first load.
      if (showGlobalLoading) {
        setLoading(true);
      }
      const response = await getDisciplines();
      // ONLY use company disciplines (loaded ones), never show templates in sidebar
      const loadedDisciplines = response.company_disciplines || [];
      
      setDisciplines(loadedDisciplines);
      
      // Set first discipline as selected if none selected (only if not controlled from parent)
      if (selectedDisciplineId === undefined && !internalSelectedDiscipline && loadedDisciplines.length > 0) {
        const firstDisciplineId = loadedDisciplines[0].id || loadedDisciplines[0].code;
        setInternalSelectedDiscipline(firstDisciplineId);
        onDisciplineSelect?.(firstDisciplineId);
      }
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      // Fallback to static data if API fails
      setDisciplines(documentDisciplines);
      if (selectedDisciplineId === undefined && !internalSelectedDiscipline && documentDisciplines.length > 0) {
        const firstDisciplineId = documentDisciplines[0].id;
        setInternalSelectedDiscipline(firstDisciplineId);
        onDisciplineSelect?.(firstDisciplineId);
      }
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  };

  const disciplineKey = (d) => d?.id ?? d?.code;

  const upsertCategoryInDiscipline = useCallback((disciplineKeyOrId, category) => {
    setDisciplines((prev) =>
      prev.map((d) => {
        if (String(disciplineKey(d)) !== String(disciplineKeyOrId)) return d;
        const cats = Array.isArray(d.categories) ? [...d.categories] : [];
        const idx = cats.findIndex((c) => String(c.id) === String(category.id));
        if (idx >= 0) {
          cats[idx] = { ...cats[idx], ...category };
        } else {
          cats.push({
            ...category,
            chapters: category.chapters || [],
          });
        }
        return { ...d, categories: cats };
      })
    );
  }, []);

  const removeChapterRecursive = (chapters, chapterId) => {
    if (!chapters || chapters.length === 0) return [];
    return chapters
      .filter((ch) => String(ch.id) !== String(chapterId))
      .map((ch) => ({
        ...ch,
        sub_chapters: removeChapterRecursive(ch.sub_chapters || ch.subChapters || [], chapterId),
        subChapters: removeChapterRecursive(ch.subChapters || ch.sub_chapters || [], chapterId),
      }));
  };

  const insertChapterIntoTree = (chapters, parentChapterId, newChapter) => {
    const list = Array.isArray(chapters) ? [...chapters] : [];
    if (!parentChapterId) {
      list.push(newChapter);
      return list;
    }
    return list.map((ch) => {
      if (String(ch.id) === String(parentChapterId)) {
        const subs = [...(ch.sub_chapters || ch.subChapters || [])];
        subs.push(newChapter);
        return { ...ch, sub_chapters: subs, subChapters: subs };
      }
      const subs = ch.sub_chapters || ch.subChapters;
      if (subs && subs.length) {
        const nextSubs = insertChapterIntoTree(subs, parentChapterId, newChapter);
        return { ...ch, sub_chapters: nextSubs, subChapters: nextSubs };
      }
      return ch;
    });
  };

  const updateChapterInTree = (chapters, updated) => {
    if (!chapters || chapters.length === 0) return [];
    return chapters.map((ch) => {
      if (String(ch.id) === String(updated.id)) {
        const existingSubs = ch.sub_chapters || ch.subChapters || [];
        const nextSubs =
          updated.sub_chapters !== undefined || updated.subChapters !== undefined
            ? updated.sub_chapters || updated.subChapters || []
            : existingSubs;
        return {
          ...ch,
          ...updated,
          sub_chapters: nextSubs,
          subChapters: nextSubs,
        };
      }
      const subs = ch.sub_chapters || ch.subChapters;
      if (subs && subs.length) {
        const next = updateChapterInTree(subs, updated);
        return { ...ch, sub_chapters: next, subChapters: next };
      }
      return ch;
    });
  };

  const chapterExistsById = (chs, id) =>
    (chs || []).some((c) => {
      if (String(c.id) === String(id)) return true;
      return chapterExistsById(c.sub_chapters || c.subChapters, id);
    });

  const upsertChapterInCategory = useCallback((categoryId, parentChapterId, chapterPayload) => {
    const normalized = {
      id: chapterPayload.id,
      number: chapterPayload.number,
      title: chapterPayload.title,
      description: chapterPayload.description,
      parent_chapter_id: chapterPayload.parent_chapter_id ?? null,
      sub_chapters: chapterPayload.sub_chapters || chapterPayload.subChapters || [],
    };
    setDisciplines((prev) =>
      prev.map((d) => {
        if (!d.categories) return d;
        return {
          ...d,
          categories: d.categories.map((cat) => {
            if (String(cat.id) !== String(categoryId)) return cat;
            const roots = cat.chapters || [];
            if (chapterExistsById(roots, normalized.id)) {
              return {
                ...cat,
                chapters: updateChapterInTree(roots, normalized),
              };
            }
            return {
              ...cat,
              chapters: insertChapterIntoTree(roots, parentChapterId, {
                ...normalized,
                sub_chapters: normalized.sub_chapters || [],
              }),
            };
          }),
        };
      })
    );
  }, []);

  const deleteChapterInCategory = useCallback((categoryId, chapterId) => {
    setDisciplines((prev) =>
      prev.map((d) => {
        if (!d.categories) return d;
        return {
          ...d,
          categories: d.categories.map((cat) => {
            if (String(cat.id) !== String(categoryId)) return cat;
            return {
              ...cat,
              chapters: removeChapterRecursive(cat.chapters || [], chapterId),
            };
          }),
        };
      })
    );
  }, []);
  const [expandedCategories, setExpandedCategories] = useState(() =>
    toStoredSet(initialSidebarStateRef.current.expandedCategories)
  );
  const [expandedChapters, setExpandedChapters] = useState(() =>
    toStoredSet(initialSidebarStateRef.current.expandedChapters)
  );
  const [selectedChapter, setSelectedChapter] = useState(
    () => initialSidebarStateRef.current.selectedChapter || null
  );
  const [contextMenu, setContextMenu] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);
  const [creatingChapter, setCreatingChapter] = useState(null); // { categoryId, parentChapterNumber, parentChapterId }
  const [creatingDiscipline, setCreatingDiscipline] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(null); // { disciplineId }
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryContextMenu, setCategoryContextMenu] = useState(null);
  const [disciplineContextMenu, setDisciplineContextMenu] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const sidebarRef = useRef(null);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        SIDEBAR_STATE_KEY,
        JSON.stringify({
          expandedCategories: Array.from(expandedCategories),
          expandedChapters: Array.from(expandedChapters),
          selectedChapter,
        })
      );
    } catch {
      // Best-effort UI state only.
    }
  }, [expandedCategories, expandedChapters, selectedChapter]);

  // Create Category Form Component
  const CreateCategoryForm = ({ disciplineId, onClose, onSuccess }) => {
    const [categoryData, setCategoryData] = useState({
      name: "",
      code: "",
      description: ""
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!categoryData.name.trim()) {
        toast.error(t("Navn er påkrevd"));
        return;
      }
      if (!categoryData.code.trim()) {
        toast.error(t("Kode er påkrevd"));
        return;
      }

      try {
        setLoading(true);
        const parsedDisciplineId =
          disciplineId != null && String(disciplineId).match(/^\d+$/)
            ? Number(disciplineId)
            : null;

        await onSuccess({
          ...categoryData,
          __disciplineKey: disciplineId,
          ...(disciplineId != null
            ? { discipline_id: parsedDisciplineId != null ? parsedDisciplineId : disciplineId }
            : {}),
        });
      } catch (err) {
        // Error handling is done in handleCreateCategory
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <PMField
          label={t("Navn")}
          value={categoryData.name}
          onChange={(e) => {
            const name = e.target.value;
            // Auto-generate code from name
            const code = name
              .toUpperCase()
              .replace(/[^A-Z0-9]/g, '_')
              .replace(/_+/g, '_')
              .replace(/^_|_$/g, '');
            setCategoryData({ ...categoryData, name, code });
          }}
          placeholder={t("Skriv inn kategori-navn")}
          required
        />
        <PMField
          label={t("Kode")}
          value={categoryData.code}
          onChange={(e) => setCategoryData({ ...categoryData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
          placeholder={t("f.eks. HMS")}
          required
        />
        <PMField
          label={t("Beskrivelse")}
          value={categoryData.description}
          onChange={(e) => setCategoryData({ ...categoryData, description: e.target.value })}
          placeholder={t("Skriv inn beskrivelse (valgfritt)")}
          multiline
          rows={3}
        />
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            {t("Avbryt")}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t("Oppretter...") : t("Opprett")}
          </button>
        </div>
      </form>
    );
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      const { __disciplineKey, ...payload } = categoryData;
      const response = await createCategory(payload);
      const created = response?.data ?? response;
      if (created?.id != null && __disciplineKey != null) {
        upsertCategoryInDiscipline(__disciplineKey, { ...created, chapters: created.chapters || [] });
      } else {
        await fetchDisciplines({ silent: true });
      }
      toast.success(t("Kategori opprettet"));
      setCreatingCategory(null);
      return response;
    } catch (err) {
      console.error("Error creating category:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke opprette kategori");
      toast.error(errorMessage);
      throw err;
    }
  };

  // Create Discipline Form Component
  const CreateDisciplineForm = ({ onClose, onSuccess }) => {
    const [disciplineData, setDisciplineData] = useState({
      name: "",
      description: "",
      code: ""
    });
    const [loading, setLoading] = useState(false);
    const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);

    // Auto-generate code from name and existing codes
    const generateCode = (name) => {
      if (!name) return "";
      
      // Base code from name
      let baseCode = name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
      
      // Check existing codes to suggest next number if similar codes exist
      const existingCodes = disciplines
        .map(d => d.code)
        .filter(code => code && code.startsWith(baseCode));
      
      if (existingCodes.length > 0) {
        // Extract numbers from existing codes (e.g., "PROSJEKTERING_VVS_1" -> 1)
        const numbers = existingCodes
          .map(code => {
            const match = code.match(/_(\d+)$/);
            return match ? parseInt(match[1]) : 0;
          })
          .filter(n => n > 0);
        
        if (numbers.length > 0) {
          const maxNumber = Math.max(...numbers);
          baseCode = `${baseCode}_${maxNumber + 1}`;
        } else {
          // If no numbers, add _1
          baseCode = `${baseCode}_1`;
        }
      }
      
      return baseCode;
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!disciplineData.name.trim()) {
        toast.error(t("Navn er påkrevd"));
        return;
      }

      try {
        setLoading(true);
        // Auto-generate code if not provided
        const finalData = {
          ...disciplineData,
          code: disciplineData.code.trim() || generateCode(disciplineData.name)
        };
        await onSuccess(finalData);
      } catch (err) {
        // Error handling is done in handleCreateDiscipline
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <PMField
          label={t("Navn")}
          value={disciplineData.name}
          onChange={(e) => {
            const name = e.target.value;
            setDisciplineData({ 
              ...disciplineData, 
              name,
              // Auto-generate code if user hasn't manually edited it
              code: codeManuallyEdited ? disciplineData.code : generateCode(name)
            });
          }}
          placeholder={t("Skriv inn navn på bransje/fag")}
          required
        />
        <PMField
          label={t("Kode")}
          value={disciplineData.code}
          onChange={(e) => {
            setCodeManuallyEdited(true);
            setDisciplineData({ ...disciplineData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') });
          }}
          placeholder={t("f.eks. PROSJEKTERING_VVS (valgfritt)")}
          helpText={t("Hvis ikke angitt, genereres automatisk fra navn og eksisterende koder")}
        />
        <PMField
          label={t("Beskrivelse")}
          value={disciplineData.description}
          onChange={(e) => setDisciplineData({ ...disciplineData, description: e.target.value })}
          placeholder={t("Skriv inn beskrivelse (valgfritt)")}
          multiline
          rows={3}
        />
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            {t("Avbryt")}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t("Oppretter...") : t("Opprett")}
          </button>
        </div>
      </form>
    );
  };

  // Create Chapter Form Component
  const CreateChapterForm = ({ categoryId, parentChapterId, parentChapterNumber, onClose, onSuccess }) => {
    const resolvedCategoryId = useMemo(() => {
      const numeric = toNumericId(categoryId);
      if (numeric) return numeric;
      const cat = findCategoryInDisciplines(disciplines, categoryId, currentDiscipline);
      return toNumericId(cat?.id);
    }, [categoryId, disciplines, currentDiscipline]);

    const [chapterData, setChapterData] = useState({
      number: "",
      title: "",
      description: ""
    });
    const [loading, setLoading] = useState(false);
    const [fetchingNumber, setFetchingNumber] = useState(true);

    useEffect(() => {
      // Fetch suggested next number
      const fetchSuggestedNumber = async () => {
        try {
          setFetchingNumber(true);
          if (!resolvedCategoryId) return;
          const response = await getNextChapterNumber(resolvedCategoryId, parentChapterId);
          setChapterData(prev => ({ ...prev, number: response.suggested_number }));
        } catch (err) {
          console.error("Error fetching suggested chapter number:", err);
          toast.error(t("Kunne ikke hente forslag på kapittelnummer"));
        } finally {
          setFetchingNumber(false);
        }
      };
      fetchSuggestedNumber();
    }, [resolvedCategoryId, parentChapterId]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!chapterData.title.trim()) {
        toast.error(t("Tittel er påkrevd"));
        return;
      }
      if (!chapterData.number.trim()) {
        toast.error(t("Kapittelnummer er påkrevd"));
        return;
      }

      try {
        setLoading(true);
        if (!resolvedCategoryId) {
          toast.error(t("Kategori ikke funnet"));
          return;
        }
        const createdResp = await createChapter(resolvedCategoryId, {
          ...chapterData,
          parent_chapter_id: parentChapterId || null
        });
        toast.success(t("Kapittel opprettet"));
        const createdChapter = createdResp?.data ?? createdResp;
        onSuccess?.(createdChapter, resolvedCategoryId, parentChapterId || null);
      } catch (err) {
        console.error("Error creating chapter:", err);
        const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke opprette kapittel");
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <PMField
          label={t("Kapittelnummer")}
          value={chapterData.number}
          onChange={(e) => setChapterData({ ...chapterData, number: e.target.value })}
          placeholder={fetchingNumber ? t("Henter forslag...") : (parentChapterNumber ? `${parentChapterNumber}.1` : "1")}
          required
        />
        {parentChapterNumber && (
          <p className="text-xs text-gray-500">
            {t("Underkapittel under")} {parentChapterNumber}
          </p>
        )}
        <PMField
          label={t("Tittel")}
          value={chapterData.title}
          onChange={(e) => setChapterData({ ...chapterData, title: e.target.value })}
          placeholder={t("Skriv inn kapittel-tittel")}
          required
        />
        <PMField
          label={t("Beskrivelse")}
          value={chapterData.description}
          onChange={(e) => setChapterData({ ...chapterData, description: e.target.value })}
          placeholder={t("Skriv inn beskrivelse (valgfritt)")}
          multiline
          rows={3}
        />
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            {t("Avbryt")}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            disabled={loading || fetchingNumber}
          >
            {loading ? t("Oppretter...") : t("Opprett")}
          </button>
        </div>
      </form>
    );
  };

  // Find current discipline, handling both string and number ID comparisons
  const currentDiscipline = disciplines.find((d) => {
    const disciplineId = d.id || d.code;
    const selectedId = selectedDiscipline;
    // Compare as both string and number to handle type mismatches
    return (
      disciplineId === selectedId ||
      String(disciplineId) === String(selectedId) ||
      Number(disciplineId) === Number(selectedId)
    );
  });

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const toggleChapter = (chapterPath) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(chapterPath)) {
      newExpanded.delete(chapterPath);
    } else {
      newExpanded.add(chapterPath);
    }
    setExpandedChapters(newExpanded);
  };

  const handleChapterClick = (e, chapter, categoryId, chapterPath) => {
    e.stopPropagation();
    setSelectedChapter(chapterPath);
    onChapterSelect?.(categoryId, chapter.number);
  };

  const handleAddChapter = (e, categoryId, parentChapterNumber) => {
    e?.stopPropagation();
    // Resolve category across all disciplines (e.g. "Alle" valgt → currentDiscipline er tom)
    const category = findCategoryInDisciplines(disciplines, categoryId, currentDiscipline);
    const resolvedCategoryId = category?.id;
    let parentChapterId = null;
    if (parentChapterNumber && category) {
      parentChapterId = findChapterIdByNumber(
        category.chapters || category.root_chapters,
        parentChapterNumber
      );
    }
    setCreatingChapter({ categoryId: resolvedCategoryId || categoryId, parentChapterNumber, parentChapterId });
  };

  const handleContextMenu = (e, chapter, categoryId, chapterPath) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position, ensuring menu stays within viewport
    const x = Math.min(e.clientX, window.innerWidth - 160); // 160px = min menu width
    const y = Math.min(e.clientY, window.innerHeight - 100); // 100px = approximate menu height
    
    setContextMenu({
      x,
      y,
      chapter,
      categoryId,
      chapterPath,
    });
  };

  const handleCategoryContextMenu = (e, category, disciplineId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position, ensuring menu stays within viewport
    const x = Math.min(e.clientX, window.innerWidth - 160);
    const y = Math.min(e.clientY, window.innerHeight - 150);
    
    setCategoryContextMenu({
      x,
      y,
      category,
      disciplineId,
    });
  };

  const handleDisciplineContextMenu = (e, discipline) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position, ensuring menu stays within viewport
    const x = Math.min(e.clientX, window.innerWidth - 160);
    const y = Math.min(e.clientY, window.innerHeight - 100);
    
    setDisciplineContextMenu({
      x,
      y,
      discipline,
    });
  };

  const handleEditChapter = () => {
    if (contextMenu) {
      setEditingChapter({
        ...contextMenu.chapter,
        categoryId: contextMenu.categoryId,
        chapterPath: contextMenu.chapterPath,
      });
      setContextMenu(null);
    }
  };

  const handleDeleteChapter = () => {
    if (contextMenu) {
      setDeleteConfirm({
        chapter: contextMenu.chapter,
        categoryId: contextMenu.categoryId,
        chapterPath: contextMenu.chapterPath,
      });
      setContextMenu(null);
    }
  };

  const handleAddDocument = () => {
    if (contextMenu) {
      onAddDocument?.(contextMenu.categoryId, contextMenu.chapter.number);
      setContextMenu(null);
    }
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        const category = findCategoryInDisciplines(
          disciplines,
          deleteConfirm.categoryId,
          currentDiscipline
        );
        if (!category) {
          toast.error(t("Kategori ikke funnet"));
          return;
        }
        await deleteChapter(category.id, deleteConfirm.chapter.id);
        toast.success(t("Kapittel slettet"));
        deleteChapterInCategory(category.id, deleteConfirm.chapter.id);
        setDeleteConfirm(null);
      } catch (err) {
        console.error("Error deleting chapter:", err);
        const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke slette kapittel");
        toast.error(errorMessage);
      }
    }
  };

  const saveEdit = async () => {
    if (editingChapter) {
      try {
        const category = findCategoryInDisciplines(
          disciplines,
          editingChapter.categoryId,
          currentDiscipline
        );
        if (!category) {
          toast.error(t("Kategori ikke funnet"));
          return;
        }
        const updatedResp = await updateChapter(category.id, editingChapter.id, {
          number: editingChapter.number,
          title: editingChapter.title,
          description: editingChapter.description || null,
        });
        toast.success(t("Kapittel oppdatert"));
        const updatedChapter = updatedResp?.data ?? updatedResp;
        if (updatedChapter?.id) {
          upsertChapterInCategory(category.id, null, updatedChapter);
        }
        setEditingChapter(null);
      } catch (err) {
        console.error("Error updating chapter:", err);
        const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke oppdatere kapittel");
        toast.error(errorMessage);
      }
    }
  };

  const handleCreateDiscipline = async (disciplineData) => {
    try {
      const response = await createDiscipline(disciplineData);
      toast.success(t("Bransje/fag opprettet"));
      const createdDiscipline = response?.data;
      if (createdDiscipline?.id != null) {
        setDisciplines((prev) => {
          if (prev.some((d) => String(d.id) === String(createdDiscipline.id))) return prev;
          return [...prev, { ...createdDiscipline, categories: createdDiscipline.categories || [] }];
        });
      } else {
        await fetchDisciplines({ silent: true });
      }
      // Select the newly created discipline
      const createdId = createdDiscipline?.id ?? response?.data?.id;
      if (createdId) {
        if (selectedDisciplineId === undefined) {
          setInternalSelectedDiscipline(createdId);
        }
        onDisciplineSelect?.(createdId);
      }
      setCreatingDiscipline(false);
      return response;
    } catch (err) {
      console.error("Error creating discipline:", err);
      const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke opprette bransje/fag");
      toast.error(errorMessage);
      throw err;
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setContextMenu(null);
      }
      if (categoryContextMenu && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setCategoryContextMenu(null);
      }
      if (disciplineContextMenu && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setDisciplineContextMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu, categoryContextMenu, disciplineContextMenu]);

  const renderChapter = (chapter, categoryId, parentPath = "") => {
    // Make selection/expansion unique across categories/disciplines
    const chapterPath = parentPath
      ? `${parentPath}.${chapter.number}`
      : `${categoryId}:${chapter.number}`;
    const isExpanded = expandedChapters.has(chapterPath);
    // Support both API structure (sub_chapters) and static structure (subChapters)
    const hasSubChapters =
      (chapter.sub_chapters && chapter.sub_chapters.length > 0) ||
      (chapter.subChapters && chapter.subChapters.length > 0);
    const isSelected = selectedChapter === chapterPath;
    const chapterText = `${chapter.number} - ${chapter.title}`;

    return (
      <div key={chapterPath} className="ml-4">
        <div
          className={`flex items-center group py-1 px-2 hover:bg-gray-100 cursor-pointer rounded text-sm ${
            isSelected ? 'bg-blue-50 border border-blue-200' : ''
          }`}
          onClick={(e) => {
            if (hasSubChapters) {
              toggleChapter(chapterPath);
            }
            handleChapterClick(e, chapter, categoryId, chapterPath);
          }}
          onContextMenu={(e) => handleContextMenu(e, chapter, categoryId, chapterPath)}
        >
          {hasSubChapters ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleChapter(chapterPath);
              }}
              className="mr-1 p-0.5 hover:bg-gray-200 rounded"
              title={isExpanded ? t("Kollaps") : t("Ekspander")}
            >
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              )}
            </button>
          ) : (
            <DocumentIcon className="h-4 w-4 text-gray-400 mr-1 flex-shrink-0" />
          )}
          <span 
            className="text-gray-700 truncate flex-1"
            title={chapterText}
          >
            {chapterText}
          </span>
          <button
            onClick={(e) => handleAddChapter(e, categoryId, chapter.number)}
            className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-blue-100 rounded text-blue-600 transition-opacity"
            title={t("Legg til underkapittel")}
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>
        {hasSubChapters && isExpanded && (
          <div className="ml-2">
            {(chapter.sub_chapters || chapter.subChapters || [])
              .slice()
              .sort((x, y) => compareChapterNumbers(x.number, y.number))
              .map((subChapter) => renderChapter(subChapter, categoryId, chapterPath))}
            {/* Add button for adding sub-chapter at this level */}
            <button
              onClick={(e) => handleAddChapter(e, categoryId, chapter.number)}
              className="ml-4 mt-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center"
              title={t("Legg til underkapittel under") + ` ${chapter.number}`}
            >
              <PlusIcon className="h-3 w-3 mr-1" />
              {t("Legg til underkapittel")}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={sidebarRef} className="w-64 bg-white border-r border-gray-200 h-full overflow-y-auto relative">
      {/* Context Menu for Chapters */}
      {contextMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[150px]"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {onAddDocument && (
              <button
                onClick={handleAddDocument}
                className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center border-b border-gray-200"
              >
                <DocumentPlusIcon className="h-4 w-4 mr-2 text-blue-500" />
                {t("Legg til dokument")}
              </button>
            )}
            <button
              onClick={() => {
                handleAddChapter(null, contextMenu.categoryId, contextMenu.chapter.number);
                setContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center border-b border-gray-200"
            >
              <PlusIcon className="h-4 w-4 mr-2 text-blue-500" />
              {t("Legg til underkapittel")}
            </button>
            <button
              onClick={handleEditChapter}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <PencilIcon className="h-4 w-4 mr-2 text-gray-500" />
              {t("Rediger")}
            </button>
            <button
              onClick={handleDeleteChapter}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
            >
              <TrashIcon className="h-4 w-4 mr-2 text-red-500" />
              {t("Slett")}
            </button>
          </div>
        </>
      )}

      {/* Context Menu for Categories */}
      {categoryContextMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setCategoryContextMenu(null)}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[150px]"
            style={{
              left: `${categoryContextMenu.x}px`,
              top: `${categoryContextMenu.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddChapter(e, categoryContextMenu.category.id ?? categoryContextMenu.category.code, null);
                setCategoryContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center border-b border-gray-200"
            >
              <PlusIcon className="h-4 w-4 mr-2 text-blue-500" />
              {t("Legg til kapittel")}
            </button>
            <button
              onClick={() => {
                setEditingCategory(categoryContextMenu.category);
                setCategoryContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <PencilIcon className="h-4 w-4 mr-2 text-gray-500" />
              {t("Rediger kategori")}
            </button>
          </div>
        </>
      )}

      {/* Context Menu for Discipline */}
      {disciplineContextMenu && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setDisciplineContextMenu(null)}
          />
          <div
            className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[150px]"
            style={{
              left: `${disciplineContextMenu.x}px`,
              top: `${disciplineContextMenu.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setCreatingCategory({ disciplineId: disciplineContextMenu.discipline?.id ?? disciplineContextMenu.discipline?.code });
                setDisciplineContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2 text-blue-500" />
              {t("Legg til kategori")}
            </button>
          </div>
        </>
      )}

      {/* Create Category Dialog */}
      <PMDialog
        open={!!creatingCategory}
        onClose={() => setCreatingCategory(null)}
        title={t("Opprett ny kategori")}
        size="md"
      >
        {creatingCategory && (
          <CreateCategoryForm
            disciplineId={creatingCategory.disciplineId}
            onClose={() => setCreatingCategory(null)}
            onSuccess={handleCreateCategory}
          />
        )}
      </PMDialog>

      {/* Create Chapter Dialog */}
      <PMDialog
        open={!!creatingChapter}
        onClose={() => setCreatingChapter(null)}
        title={t("Legg til nytt kapittel")}
        size="md"
      >
        {creatingChapter && (
          <CreateChapterForm
            categoryId={creatingChapter.categoryId}
            parentChapterId={creatingChapter.parentChapterId}
            parentChapterNumber={creatingChapter.parentChapterNumber}
            onClose={() => setCreatingChapter(null)}
            onSuccess={(createdChapter, resolvedCategoryId, parentId) => {
              if (createdChapter && resolvedCategoryId) {
                upsertChapterInCategory(resolvedCategoryId, parentId, createdChapter);
              }
              setCreatingChapter(null);
            }}
          />
        )}
      </PMDialog>

      {/* Edit Chapter Dialog */}
      <PMDialog
        open={!!editingChapter}
        onClose={() => setEditingChapter(null)}
        title={t("Rediger kapittel")}
        size="md"
      >
        {editingChapter && (
          <div className="space-y-4">
            <PMField
              label={t("Kapittelnummer")}
              value={editingChapter.number}
              onChange={(e) =>
                setEditingChapter({ ...editingChapter, number: e.target.value })
              }
              placeholder="f.eks. 6.1"
            />
            <PMField
              label={t("Tittel")}
              value={editingChapter.title}
              onChange={(e) =>
                setEditingChapter({ ...editingChapter, title: e.target.value })
              }
              placeholder={t("Skriv inn kapittel-tittel")}
              required
            />
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setEditingChapter(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t("Avbryt")}
              </button>
              <button
                onClick={saveEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                {t("Lagre")}
              </button>
            </div>
          </div>
        )}
      </PMDialog>

      {/* Edit Category Dialog */}
      <PMDialog
        open={!!editingCategory}
        onClose={() => setEditingCategory(null)}
        title={t("Rediger kategori")}
        size="md"
      >
        {editingCategory && (
          <div className="space-y-4">
            <PMField
              label={t("Navn")}
              value={editingCategory.name || ""}
              onChange={(e) =>
                setEditingCategory({ ...editingCategory, name: e.target.value })
              }
              placeholder={t("Skriv inn kategori-navn")}
              required
            />
            <PMField
              label={t("Beskrivelse")}
              value={editingCategory.description || ""}
              onChange={(e) =>
                setEditingCategory({ ...editingCategory, description: e.target.value })
              }
              placeholder={t("Skriv inn beskrivelse (valgfritt)")}
              multiline
              rows={3}
            />
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setEditingCategory(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t("Avbryt")}
              </button>
              <button
                onClick={async () => {
                  try {
                    const upd = await updateCategory(editingCategory.id, {
                      name: editingCategory.name,
                      description: editingCategory.description || null,
                    });
                    toast.success(t("Kategori oppdatert"));
                    const updatedCategory = upd?.data ?? upd;
                    if (updatedCategory?.id) {
                      setDisciplines((prev) =>
                        prev.map((d) => {
                          if (!d.categories) return d;
                          return {
                            ...d,
                            categories: d.categories.map((c) =>
                              String(c.id) === String(editingCategory.id) ? { ...c, ...updatedCategory } : c
                            ),
                          };
                        })
                      );
                    }
                    setEditingCategory(null);
                  } catch (err) {
                    console.error("Error updating category:", err);
                    const errorMessage = err.response?.data?.errors?.[0] || err.message || t("Kunne ikke oppdatere kategori");
                    toast.error(errorMessage);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
              >
                {t("Lagre")}
              </button>
            </div>
          </div>
        )}
      </PMDialog>

      {/* Delete Confirmation Dialog */}
      <PMDialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title={t("Bekreft sletting")}
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("Er du sikker på at du vil slette kapittel")} <strong>{deleteConfirm.chapter.number} - {deleteConfirm.chapter.title}</strong>?
            </p>
            {deleteConfirm.chapter.subChapters && deleteConfirm.chapter.subChapters.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-xs text-yellow-800">
                  {t("Advarsel: Dette kapittelet har underkapitler. Alle underkapitler vil også bli slettet.")}
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {t("Avbryt")}
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                {t("Slett")}
              </button>
            </div>
          </div>
        )}
      </PMDialog>

      {/* Create Discipline Dialog */}
      <PMDialog
        open={creatingDiscipline}
        onClose={() => setCreatingDiscipline(false)}
        title={t("Opprett nytt bransje/fag")}
        size="md"
      >
        <CreateDisciplineForm
          onClose={() => setCreatingDiscipline(false)}
          onSuccess={handleCreateDiscipline}
        />
      </PMDialog>

      {/* Discipline Selector */}
      <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-semibold text-gray-700">
            {t("Fag/Bransje")}
          </label>
          <button
            onClick={() => setCreatingDiscipline(true)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
            title={t("Opprett nytt bransje/fag")}
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            {t("Ny")}
          </button>
        </div>
        {loading ? (
          <div className="text-sm text-gray-500">{t("Laster...")}</div>
        ) : (
          <select
            value={selectedDiscipline || ""}
            onChange={(e) => {
              const disciplineId = e.target.value === "" ? null : e.target.value;
              if (selectedDisciplineId === undefined) {
                // Only update internal state if not controlled from parent
                setInternalSelectedDiscipline(disciplineId);
              }
              // Always call callback to notify parent (null for "Alle")
              onDisciplineSelect?.(disciplineId);
              setExpandedCategories(new Set());
              setExpandedChapters(new Set());
            }}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
          >
            <option value="">{t("Alle")}</option>
            {disciplines.map((discipline) => (
              <option key={discipline.id ?? discipline.code} value={discipline.id ?? discipline.code}>
                {discipline.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Treeview */}
      <div className="p-2">
        {loading ? (
          <div className="text-sm text-gray-500 text-center py-4">{t("Laster struktur...")}</div>
        ) : !currentDiscipline && selectedDiscipline ? (
          <div className="text-sm text-gray-500 text-center py-4">{t("Ingen struktur tilgjengelig")}</div>
        ) : !selectedDiscipline ? (
          // Show all disciplines when "Alle" is selected
          disciplines.map((discipline) => (
            <div key={discipline.id ?? discipline.code} className="mb-4">
              <div 
                className="font-semibold text-sm text-gray-700 mb-2 px-2 flex items-center justify-between group"
                onContextMenu={(e) => handleDisciplineContextMenu(e, discipline)}
              >
                <span>{discipline.name}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreatingCategory({ disciplineId: discipline.id ?? discipline.code });
                  }}
                  className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-blue-100 rounded text-blue-600 transition-opacity flex items-center"
                  title={t("Legg til kategori")}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  <span className="text-xs">{t("Kategori")}</span>
                </button>
              </div>
              {discipline.categories && discipline.categories.length > 0 ? (
                discipline.categories.map((category) => {
                  const categoryKey = `${discipline.id ?? discipline.code}_${category.id ?? category.code}`;
                  const isCategoryExpanded = expandedCategories.has(categoryKey);
                  
                  return (
                    <div key={categoryKey} className="mb-2">
                      <div
                        className="flex items-center py-2 px-2 hover:bg-gray-100 cursor-pointer rounded font-medium text-sm"
                        onClick={() => toggleCategory(categoryKey)}
                        onContextMenu={(e) => handleCategoryContextMenu(e, category, discipline.id ?? discipline.code)}
                      >
                        {isCategoryExpanded ? (
                          <ChevronDownIcon className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" />
                        )}
                        <FolderIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{category.name}</span>
                      </div>
                      {isCategoryExpanded && category.chapters && (
                        <div className="ml-6">
                          {/* Only show root chapters (main chapters without parent) */}
                          {category.chapters
                            .filter(chapter => !chapter.parent_chapter_id && !chapter.parentChapterId)
                            .slice()
                            .sort((x, y) => compareChapterNumbers(x.number, y.number))
                            .map((chapter) =>
                              renderChapter(chapter, category.id)
                            )}
                          {/* Add button for adding top-level chapter */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddChapter(e, category.id ?? category.code, null);
                            }}
                            className="ml-4 mt-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center"
                          >
                            <PlusIcon className="h-3 w-3 mr-1" />
                            {t("Legg til kapittel")}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-2 px-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreatingCategory({ disciplineId: discipline.id ?? discipline.code });
                    }}
                    className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center mx-auto"
                    title={t("Legg til kategori")}
                  >
                    <PlusIcon className="h-3 w-3 mr-1" />
                    {t("Legg til kategori")}
                  </button>
                </div>
              )}
            </div>
          )) 
        ) : currentDiscipline ? (
          <>
            {/* Discipline header with add category button */}
            <div className="mb-3 pb-2 border-b border-gray-200">
              <div 
                className="flex items-center justify-between px-2 group cursor-pointer"
                onContextMenu={(e) => handleDisciplineContextMenu(e, currentDiscipline)}
              >
                <h3 className="font-semibold text-sm text-gray-700 flex-1">
                  {currentDiscipline.name}
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreatingCategory({ disciplineId: currentDiscipline?.id ?? currentDiscipline?.code });
                  }}
                  className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-blue-100 rounded text-blue-600 transition-opacity flex items-center"
                  title={t("Legg til kategori")}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  <span className="text-xs">{t("Kategori")}</span>
                </button>
              </div>
            </div>

            {/* Categories list or empty state */}
            {currentDiscipline.categories && currentDiscipline.categories.length > 0 ? (
              currentDiscipline.categories.map((category) => {
                const categoryKey = category.id ?? category.code;
                const isCategoryExpanded = expandedCategories.has(categoryKey);

                return (
                  <div key={categoryKey} className="mb-2">
                    <div
                      className="flex items-center group py-2 px-2 hover:bg-gray-100 cursor-pointer rounded font-medium text-sm"
                      onClick={() => {
                        toggleCategory(categoryKey);
                        onCategorySelect?.(category.id ?? category.code);
                      }}
                      onContextMenu={(e) => handleCategoryContextMenu(e, category, currentDiscipline?.id ?? currentDiscipline?.code)}
                    >
                      {isCategoryExpanded ? (
                        <ChevronDownIcon 
                          className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0" 
                          title={t("Kollaps kategori")}
                        />
                      ) : (
                        <ChevronRightIcon 
                          className="h-4 w-4 text-gray-500 mr-2 flex-shrink-0"
                          title={t("Ekspander kategori")}
                        />
                      )}
                      <FolderIcon className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                      <span 
                        className="text-gray-900 truncate flex-1"
                        title={category.name}
                      >
                        {category.name}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCreatingCategory({ disciplineId: currentDiscipline?.id ?? currentDiscipline?.code });
                        }}
                        className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-blue-100 rounded text-blue-600 transition-opacity"
                        title={t("Legg til kategori")}
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>

                    {isCategoryExpanded && (
                      <div className="ml-2">
                        {/* Only show root chapters (main chapters without parent) */}
                        {category.chapters
                          ?.filter(chapter => !chapter.parent_chapter_id && !chapter.parentChapterId)
                          ?.slice()
                          ?.sort((x, y) => compareChapterNumbers(x.number, y.number))
                          ?.map((chapter) =>
                            renderChapter(chapter, category.id)
                          )}
                        {/* Add button for adding top-level chapter */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddChapter(e, category.id ?? category.code, null);
                          }}
                          className="ml-4 mt-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded flex items-center"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          {t("Legg til kapittel")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 px-4">
                <p className="text-sm text-gray-500 mb-3">{t("Ingen kategorier tilgjengelig")}</p>
                <button
                  onClick={() => setCreatingCategory({ disciplineId: currentDiscipline?.id ?? currentDiscipline?.code })}
                  className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 flex items-center mx-auto"
                  title={t("Legg til kategori")}
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t("Legg til kategori")}
                </button>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

